<template>
  <!-- Créer ou modifier un profil, dans un panneau latéral : depuis la liste des profils, la politique ou la mise en route -->
  <v-navigation-drawer :value="value" fixed right temporary width="640" class="equipment-profile-editor" @input="$emit('input', $event)">
    <v-toolbar flat>
      <v-toolbar-title class="text-h6">{{ editing.id ? $t('onboarding.equipment-profile.edit') : $t('onboarding.equipment-profile.new') }}</v-toolbar-title>
      <v-spacer />
      <v-btn icon :aria-label="$t('close')" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
    </v-toolbar>
    <v-divider />

    <v-form ref="form" class="pa-4" @submit.prevent="save">
      <v-text-field v-model="editing.name" :label="$t('onboarding.equipment-profile.name')" @input="nameTouched = true" :hint="$t('onboarding.equipment-profile.name-hint')" persistent-hint outlined dense counter="80" class="mb-2" />
      <v-select
          :value="familyOf(editing.kind)"
          :items="familyItems"
          :label="$t('onboarding.equipment-profile.kind')"
          :disabled="!!editing.id || !!kind"
          :hint="editing.id ? $t('onboarding.equipment-profile.kind-fixed') : ''"
          persistent-hint
          outlined dense class="mb-2"
          @change="familyChanged"
      />

      <!-- Un véhicule : un service, rien à poser (la personne connecte sa voiture) -->
      <template v-if="isVehicle(editing.kind)">
        <div class="text-subtitle-2 mt-2 mb-1">{{ $t('onboarding.equipment-profile.service-title') }}</div>
        <v-radio-group v-model="editing.kind" class="mt-0" hide-details :disabled="!!editing.id || !!kind" @change="kindChanged">
          <v-radio v-for="service in VEHICLE_SERVICES" :key="service" :value="service">
            <template #label>
              <div>
                <div>{{ $t(`onboarding.equipment-profile.service.${service}`) }}</div>
                <div class="text-caption text--secondary">{{ $t(`onboarding.equipment-profile.service-explained.${service}`) }}</div>
              </div>
            </template>
          </v-radio>
        </v-radio-group>
        <p class="text-caption text--secondary mt-2 mb-0">{{ $t('onboarding.equipment-profile.vehicle-activated') }}</p>
      </template>

      <!-- Une borne, un compteur : tous ses types, ou certains (une prise connectée n'a pas l'abonnement d'une borne) -->
      <template v-if="deviceTypesOf(editing.kind).length">
        <div class="text-subtitle-2 mt-4 mb-1">{{ $t(`onboarding.equipment-profile.device-types-title.${editing.kind}`) }}</div>
        <v-radio-group :value="editing.deviceTypes.length || editing.someDeviceTypes ? 'some' : 'all'" row dense hide-details class="mt-0" @change="setDeviceTypesMode">
          <v-radio value="all" :label="$t(`onboarding.equipment-profile.device-types-all.${editing.kind}`)" />
          <v-radio value="some" :label="$t(`onboarding.equipment-profile.device-types-some.${editing.kind}`)" />
        </v-radio-group>
        <v-chip-group v-if="editing.deviceTypes.length || editing.someDeviceTypes" v-model="editing.deviceTypes" multiple column active-class="primary--text" @change="deviceTypesChanged">
          <v-chip v-for="deviceType in deviceTypesOf(editing.kind)" :key="deviceType" :value="deviceType" small filter outlined>{{ deviceTypeLabel(deviceType) }}</v-chip>
        </v-chip-group>
        <p class="text-caption text--secondary mb-0">{{ $t('onboarding.equipment-profile.device-types-explained') }}</p>
      </template>

      <template v-if="!isVehicle(editing.kind)">
        <div class="text-subtitle-2 mt-4 mb-1">{{ $t('onboarding.equipment-profile.usage-title') }}</div>
        <v-radio-group v-model="editing.usage" class="mt-0" hide-details :disabled="!!usage" @change="usageChanged">
          <v-radio v-for="item in usagesFor(editing.kind)" :key="item" :value="item">
            <template #label>
              <div>
                <div>{{ $t(`onboarding.equipment-profile.usage.${item}`) }}</div>
                <div class="text-caption text--secondary">{{ $t(`onboarding.equipment-profile.usage-explained.${item}`) }}</div>
              </div>
            </template>
          </v-radio>
        </v-radio-group>
      </template>

      <!-- Une borne : publiée ou non selon l'usage ; privée ou partagée, les groupes qui y chargent -->
      <template v-if="editing.kind === 'charging-point'">
        <p class="text-caption text--secondary mt-2 mb-0">
          <v-icon x-small class="mr-1">{{ ['public', 'shared'].includes(editing.usage) ? 'mdi-earth' : 'mdi-lock-outline' }}</v-icon>{{ $t(`onboarding.equipment-profile.publication.${editing.usage}`) }}
        </p>
        <template v-if="grantsAccess">
          <div class="text-subtitle-2 mt-4 mb-1">{{ $t('onboarding.equipment-profile.access-title') }}</div>
          <p class="text-caption text--secondary mb-2">{{ $t(`onboarding.equipment-profile.access-explained.${editing.usage}`) }}</p>
          <v-row v-for="(grant, i) in editing.access" :key="i" dense align="center">
            <v-col cols="12" sm="6">
              <v-select v-model="grant.groupId" :items="memberGroups" item-value="id" item-text="name" :label="$t('onboarding.equipment-profile.access-group')" outlined dense hide-details />
            </v-col>
            <v-col cols="10" sm="5">
              <v-btn-toggle v-model="grant.free" mandatory dense color="primary">
                <v-btn small :value="true">{{ $t('onboarding.equipment-profile.access-free') }}</v-btn>
                <v-btn small :value="false">{{ $t('onboarding.equipment-profile.access-paid') }}</v-btn>
              </v-btn-toggle>
            </v-col>
            <v-col cols="2" sm="1" class="text-right">
              <v-btn icon small :aria-label="$t('onboarding.equipment-profile.access-remove')" @click="editing.access.splice(i, 1)"><v-icon small>mdi-close</v-icon></v-btn>
            </v-col>
          </v-row>
          <v-btn small text color="primary" class="px-0" :disabled="!memberGroups.length" @click="editing.access.push({ groupId: null, free: true })">
            <v-icon left small>mdi-plus</v-icon>{{ $t('onboarding.equipment-profile.access-add') }}
          </v-btn>
          <p class="text-caption text--secondary mb-0">{{ $t('onboarding.equipment-profile.access-paid-hint') }}</p>
        </template>
      </template>

      <!-- La licence : un abonnement de l'organisation, ou un nouveau saisi ici (créé avec le profil, en une fois) -->
      <div class="text-subtitle-2 mt-4 mb-1">{{ $t('onboarding.equipment-profile.licence-title') }}</div>
      <p class="text-caption text--secondary mb-2">{{ $t('onboarding.equipment-profile.licence-explained') }}</p>
      <v-radio-group v-model="editing.licenceMode" row dense hide-details class="mt-0 mb-3" @change="licenceModeChanged">
        <v-radio value="existing" :disabled="!loadingReferences && !hasCompatibleSubscription" :label="$t('onboarding.equipment-profile.licence-existing')" />
        <v-radio value="new" :label="$t('onboarding.equipment-profile.licence-new')" />
      </v-radio-group>
      <p v-if="!loadingReferences && !hasCompatibleSubscription" class="text-caption text--secondary">{{ $t('onboarding.equipment-profile.licence-none-yet') }}</p>

      <licence-setting-editor
          v-if="editing.licenceMode === 'existing'"
          :value="editing.licences"
          :kinds="[editing.kind]"
          :subscriptions="subscriptions"
          :vehicle-formulas="vehicleFormulas"
          :loading="loadingReferences"
          :licence-types="effectiveLicenceTypes"
          hide-kind
          hide-no-subscription
          @change="editing.licences = $event"
      />
      <template v-else>
        <v-progress-linear v-if="loadingPlans" indeterminate class="mb-2" />
        <v-alert v-else-if="!plans.length" type="warning" dense text class="mb-2">{{ $t('onboarding.equipment-profile.no-plan') }}</v-alert>
        <subscription-plan-fields v-model="editing.subscription" :plans="plans" :loading="loadingPlans" :col-field="8" with-formula compact />
      </template>

      <!-- « Services de recharge » : qui paie l'abonnement, qui paie les consommations (le membre de l'onboarding, ou une organisation) -->
      <div v-if="chargingServices" class="mt-4">
        <div class="text-subtitle-2 mb-1">{{ $t('onboarding.equipment-profile.billing.title') }}</div>
        <p class="text-caption text--secondary mb-2">{{ $t('onboarding.equipment-profile.billing.explained') }}</p>
        <v-row v-for="part in billingParts" :key="part" dense align="center" class="mb-1">
          <v-col cols="12" sm="4" class="text-body-2">{{ $t(`onboarding.equipment-profile.billing.${part === 'subscription' && !editing.splitConsumption ? 'recipient' : part}`) }}</v-col>
          <v-col cols="12" sm="8">
            <v-btn-toggle v-model="editing.billing[part].to" dense color="primary" @change="recipientChanged(part)">
              <v-btn small value="member" class="text-none" :disabled="editing.usage !== 'home'">{{ $t('onboarding.equipment-profile.billing.member') }}</v-btn>
              <v-btn small value="organization" class="text-none">{{ $t('onboarding.equipment-profile.billing.organization') }}</v-btn>
            </v-btn-toggle>
            <organization-select
                v-if="editing.billing[part].to === 'organization'"
                v-model="editing.billing[part].organizationId"
                :label="$t('onboarding.equipment-profile.billing.which-organization')"
                outlined dense class="mt-2"
            />
          </v-col>
        </v-row>
        <v-checkbox v-model="editing.splitConsumption" :label="$t('onboarding.equipment-profile.billing.split')" dense hide-details class="mt-0" />
        <p v-if="editing.usage !== 'home'" class="text-caption text--secondary mb-0">{{ $t('onboarding.equipment-profile.billing.member-at-home-only') }}</p>
      </div>

      <v-alert v-if="editing.error" type="error" dense outlined class="mt-3 mb-0">{{ editing.error }}</v-alert>

      <div class="d-flex mt-4" style="gap: 8px">
        <v-spacer />
        <v-btn small text @click="$emit('input', false)">{{ $t('cancel') }}</v-btn>
        <v-btn small color="primary" type="submit" :loading="editing.running" :disabled="!canSave">{{ $t('save') }}</v-btn>
      </div>
    </v-form>
  </v-navigation-drawer>
</template>

<script>
import CommissioningPolicyRepository from '@repository/CommissioningPolicyRepository'
import EquipmentProfileRepository from '@repository/EquipmentProfileRepository'
import MemberGroupRepository from '@repository/MemberGroupRepository'
import SubscriptionPlanFields, { blankSubscription, subscriptionBody } from '@blocks/SubscriptionPlanFields.vue'
import CommissioningMixin, { LICENCE_TYPES } from './CommissioningMixin'
import LicenceSettingEditor from './LicenceSettingEditor.vue'
import OrganizationSelect from '@blocks/Select/OrganizationSelect.vue'
import { deviceTypesByKind } from './Commissioning/policyCatalogue'

/** Les deux factures d'une licence « Services de recharge ». */
const BILLING_PARTS = ['subscription', 'consumption']

/*
 * Les usages permis par équipement (EquipmentProfile::USAGES côté serveur, qui reste juge) : une borne
 * sert à tout ; un compteur ne sert qu'à rembourser la recharge à domicile. Un véhicule n'a pas d'usage mais un
 * service (télématique, ou compteur embarqué qui la contient) : en interne, « à domicile », la voiture de la personne.
 */
const USAGES = {
  'charging-point': ['home', 'private', 'public', 'shared'],
  'meter-device': ['home'],
  'connected-vehicle': ['home'],
  'vehicle-meter': ['home'],
}
/** Les deux services d'un véhicule, du plus simple au plus complet. */
export const VEHICLE_SERVICES = ['connected-vehicle', 'vehicle-meter']
/** Les types d'équipement à l'écran : borne, compteur, véhicule (ses deux services). */
export const FAMILIES = [
  { family: 'charging-point', kinds: ['charging-point'], icon: 'mdi-ev-station' },
  { family: 'meter-device', kinds: ['meter-device'], icon: 'mdi-meter-electric-outline' },
  { family: 'vehicle', kinds: VEHICLE_SERVICES, icon: 'mdi-car-electric-outline' },
]

/**
 * L'éditeur d'un profil d'équipement, l'assistant unique de la chaîne abonnement → profil → politique : nom, type,
 * sous-types, usage, accès, licence — un abonnement existant ou un nouveau saisi sur place, enregistré avec le
 * profil. Utilisé par la liste des profils, la politique de recharge (type et usage imposés) et la mise en route.
 * Émet `saved` avec l'identifiant du profil.
 */
export default {
  name: 'EquipmentProfileEditor',

  components: { LicenceSettingEditor, SubscriptionPlanFields, OrganizationSelect },

  mixins: [CommissioningMixin],

  props: {
    value: { type: Boolean, default: false },
    organizationId: { type: String, required: true },
    /** Le profil à modifier (ou à dupliquer avec `copy`) ; absent : un nouveau. */
    profile: { type: Object, default: null },
    copy: { type: Boolean, default: false },
    /** Le type d'équipement imposé (depuis la politique). */
    kind: { type: String, default: null },
    /** L'usage imposé (`home` depuis la politique). */
    usage: { type: String, default: null },
    /** Les sous-types proposés d'emblée (un profil pour ce sous-type, depuis la politique). */
    deviceTypes: { type: Array, default: () => [] },
    /** Les types de licence permis (`device` : remboursement) ; absent : ceux de l'usage. */
    licenceTypes: { type: Array, default: null },
  },

  data() {
    return {
      VEHICLE_SERVICES,
      BILLING_PARTS,
      editing: this.blank(),
      // La personne a écrit son propre nom : la proposition ne le remplace plus.
      nameTouched: false,
      subscriptions: [],
      vehicleFormulas: [],
      memberGroups: [],
      deviceTypesMap: {},
      loadingReferences: false,
      plans: [],
      loadingPlans: false,
      plansRequest: 0,
    }
  },

  computed: {
    familyItems() {
      return FAMILIES.map(f => ({ value: f.family, text: this.familyLabel(f.family) }))
    },

    /** Le remboursement ne vaut qu'à domicile : ailleurs, l'abonnement de remboursement n'est pas proposé. */
    effectiveLicenceTypes() {
      const byUsage = this.editing.usage === 'home' ? null : ['charging-point', 'energy-provider']
      if (!this.licenceTypes) return byUsage
      return byUsage ? this.licenceTypes.filter(t => byUsage.includes(t)) : this.licenceTypes
    },

    /** Un abonnement de l'organisation qui convient déjà : sinon, « Nouvel abonnement » d'office. */
    hasCompatibleSubscription() {
      if (this.isVehicle(this.editing.kind)) return this.vehicleFormulas.some(f => f.kind === this.editing.kind)
      const types = (LICENCE_TYPES[this.editing.kind] || []).filter(t => !this.effectiveLicenceTypes || this.effectiveLicenceTypes.includes(t))
      return this.subscriptions.some(s => types.includes(s.licenceType))
    },

    licence() {
      return this.editing.licences[this.editing.kind] || null
    },

    /**
     * D3 : un nom proposé tiré de ce qui est choisi (équipement, usage, formule), par exemple « Borne · À domicile ·
     * Remboursement ». Le nom reste libre : dès que la personne écrit le sien, la proposition ne le touche plus.
     */
    suggestedName() {
      const kind = this.editing.kind
      const parts = [this.familyLabel(this.familyOf(kind))]
      if (!this.isVehicle(kind) && this.editing.usage) parts.push(this.$t(`onboarding.equipment-profile.usage.${this.editing.usage}`))
      parts.push(this.formulaName)
      return parts.filter(Boolean).join(' · ')
    },

    /** Le nom de la formule choisie : celle d'un abonnement existant, ou celle du nouvel abonnement saisi ici. */
    formulaName() {
      if (this.editing.licenceMode === 'new') {
        const plan = this.editing.subscription?.subscriptionPlan
        return (plan?.formulas || []).find(f => f.id === this.editing.subscription.formulaId)?.name || null
      }
      const licence = this.licence
      if (!licence?.formulaId) return null
      const vehicle = (this.vehicleFormulas || []).find(f => f.subscriptionId === licence.subscriptionId && f.formulaId === licence.formulaId)
      if (vehicle) return vehicle.formulaName || null
      const subscription = (this.subscriptions || []).find(sub => sub.id === licence.subscriptionId)
      return (subscription?.formulas || []).find(f => f.id === licence.formulaId)?.name || null
    },

    /** Une borne privée ou partagée donne accès à des groupes ; à domicile ou en public, non. */
    grantsAccess() {
      return this.editing.kind === 'charging-point' && ['private', 'shared'].includes(this.editing.usage)
    },

    /** Un abonnement « Services de recharge » (ZEborne fournit l'électricité) : le profil dit qui paie quoi. */
    chargingServices() {
      if (this.editing.licenceMode === 'new') return this.editing.subscription.subscriptionPlan?.licenceType === 'energy-provider'
      return this.subscriptions.find(s => s.id === this.licence?.subscriptionId)?.licenceType === 'energy-provider'
    },

    /** Un destinataire pour tout ; un second pour les consommations, s'il le faut. */
    billingParts() {
      return this.editing.splitConsumption ? BILLING_PARTS : ['subscription']
    },

    /** Le destinataire est obligatoire ; celui des consommations, seulement s'il est demandé. */
    billingReady() {
      return !this.chargingServices || this.billingParts.every(part => {
        const target = this.editing.billing[part]
        if (!target.to) return false
        return target.to === 'member' ? this.editing.usage === 'home' : !!target.organizationId
      })
    },

    licenceReady() {
      if (!this.billingReady) return false
      if (this.editing.licenceMode === 'existing') return !!(this.licence?.subscriptionId && this.licence?.formulaId)
      const s = this.editing.subscription
      return !!(s.subscriptionPlan && s.formulaId && s.billingCycle && s.activationStartDate && s.billingStartDate)
    },

    canSave() {
      return !!(this.editing.name.trim() && this.editing.kind && this.editing.usage && this.licenceReady)
        && (!this.grantsAccess || this.editing.access.every(g => g.groupId))
        && !(this.editing.someDeviceTypes && !this.editing.deviceTypes.length)
    },
  },

  watch: {
    // Un nouveau profil prend le nom proposé tant que la personne n'a pas écrit le sien.
    suggestedName(name) {
      if (!this.editing.id && !this.nameTouched) this.editing.name = name
    },
    value: {
      immediate: true,
      handler(open) {
        if (open) this.open()
      },
    },
  },

  methods: {
    blank() {
      return {
        id: null,
        name: '',
        kind: 'charging-point',
        usage: 'home',
        deviceTypes: [],
        someDeviceTypes: false,
        licenceMode: 'existing',
        licences: {},
        subscription: blankSubscription(),
        billing: this.defaultBilling(),
        splitConsumption: false,
        access: [],
        running: false,
        error: null,
      }
    },

    /** Rien de choisi d'avance : qui paie se décide. */
    defaultBilling() {
      return {
        subscription: { to: null, organizationId: null },
        consumption: { to: null, organizationId: null },
      }
    },

    billingOf(billing) {
      return Object.fromEntries(BILLING_PARTS.map(part => [part, {
        to: billing?.[part]?.to || null,
        organizationId: billing?.[part]?.organizationId || null,
      }]))
    },

    /** Une organisation qui paie : celle du profil d'abord, qu'on change si besoin. */
    recipientChanged(part) {
      const target = this.editing.billing[part]
      if (target.to === 'organization' && !target.organizationId) target.organizationId = this.organizationId
    },

    /** Ce que le serveur attend : le destinataire, et celui des consommations seulement s'il est à part. */
    billingBody() {
      return Object.fromEntries(this.billingParts.map(part => {
        const target = this.editing.billing[part]
        return [part, target.to === 'organization' ? { to: 'organization', organizationId: target.organizationId } : { to: 'member' }]
      }))
    },

    /** À chaque ouverture : le profil (ou un nouveau, avec ce qui est imposé), puis ce qu'il faut pour le régler. */
    async open() {
      const profile = this.profile
      this.editing = profile
        ? {
          ...this.blank(),
          id: this.copy ? null : profile.id,
          name: this.copy ? this.$t('onboarding.equipment-profile.copy-name', { name: profile.name }) : profile.name,
          kind: profile.kind,
          usage: profile.usage,
          deviceTypes: [...(profile.deviceTypes || [])],
          someDeviceTypes: (profile.deviceTypes || []).length > 0,
          licences: { [profile.kind]: { subscriptionId: profile.licence.subscriptionId, formulaId: profile.licence.formulaId, optionIds: profile.licence.optionIds || [] } },
          billing: this.billingOf(profile.licence.billing),
          splitConsumption: !!profile.licence.billing?.consumption,
          access: (profile.access || []).map(grant => ({ ...grant })),
        }
        : {
          ...this.blank(),
          kind: this.kind || 'charging-point',
          usage: this.usage || (USAGES[this.kind] || ['home'])[0],
          deviceTypes: [...this.deviceTypes],
          someDeviceTypes: this.deviceTypes.length > 0,
        }
      // Le nom d'un profil existant est le sien ; un nouveau reçoit la proposition, jusqu'à ce qu'on en écrive un.
      this.nameTouched = !!profile
      if (!profile) this.editing.name = this.suggestedName
      this.$refs.form?.resetValidation()
      await this.loadReferences()
      // Rien à quoi rattacher la licence : on la saisit ici, sans détour.
      if (!this.editing.id && !this.hasCompatibleSubscription) {
        this.editing.licenceMode = 'new'
        this.loadPlans()
      }
    },

    async loadReferences() {
      this.loadingReferences = true
      const [subscriptions, vehicleFormulas, groups, deviceTypes] = await Promise.all([
        CommissioningPolicyRepository.licenceSubscriptions(this.organizationId).catch(() => []),
        CommissioningPolicyRepository.vehicleFormulas(this.organizationId).catch(() => []),
        MemberGroupRepository.search({ organizationId: this.organizationId, offset: 0, limit: 200 }).catch(() => ({ items: [] })),
        deviceTypesByKind(),
      ])
      this.subscriptions = subscriptions
      this.vehicleFormulas = vehicleFormulas
      this.memberGroups = (groups.items || []).map(g => ({ id: g.id, name: g.name }))
      this.deviceTypesMap = deviceTypes || {}
      this.loadingReferences = false
    },

    /** Les modèles d'abonnement qui conviennent à ce profil (type, usage, sous-types) : le serveur les choisit. */
    async loadPlans() {
      if (this.editing.licenceMode !== 'new') return
      const request = ++this.plansRequest
      this.loadingPlans = true
      try {
        const plans = await EquipmentProfileRepository.subscriptionPlans(this.organizationId, {
          kind: this.editing.kind,
          usage: this.editing.usage,
          deviceTypes: this.editing.deviceTypes,
        })
        if (request !== this.plansRequest) return
        // Depuis la politique, « borne fournie » : on s'en tient aux types de licence demandés (pas de remboursement).
        const types = this.effectiveLicenceTypes
        this.plans = (plans || []).filter(p => !types || types.includes(p.licenceType))
        // Le modèle choisi ne convient plus : on repart de zéro (dates gardées).
        const chosen = this.editing.subscription.subscriptionPlan
        const still = chosen ? this.plans.find(p => p.id === chosen.id) : null
        if (!still) {
          this.editing.subscription = { ...this.editing.subscription, subscriptionPlan: null, formulaId: null, optionIds: [], customFields: [] }
          if (this.plans.length === 1) this.choosePlan(this.plans[0])
        }
      } catch (e) {
        if (request === this.plansRequest) {
          this.plans = []
          this.editing.error = this.messageOf(e)
        }
      } finally {
        if (request === this.plansRequest) this.loadingPlans = false
      }
    },

    /** Un seul modèle possible : déjà choisi, avec son cycle, ses réglages et sa seule formule. */
    choosePlan(plan) {
      const formulas = (plan.formulas || []).filter(f => f.enabled !== false && (!plan.eligibleFormulaIds || plan.eligibleFormulaIds.includes(f.id)))
      this.editing.subscription = {
        ...this.editing.subscription,
        subscriptionPlan: plan,
        customFields: plan.customFields ? this.cloneObj(plan.customFields) : [],
        billingCycle: plan.defaultBillingPeriod ?? 'MONTHLY',
        formulaId: formulas.length === 1 ? formulas[0].id : null,
        optionIds: [],
      }
    },

    usagesFor(kind) {
      return USAGES[kind] || []
    },

    deviceTypesOf(kind) {
      return this.deviceTypesMap[kind] || []
    },

    deviceTypeLabel(deviceType) {
      const key = `onboarding.device-type.${deviceType}`
      return this.$te(key) ? this.$t(key) : deviceType
    },

    isVehicle(kind) {
      return VEHICLE_SERVICES.includes(kind)
    },

    familyOf(kind) {
      return this.isVehicle(kind) ? 'vehicle' : kind
    },

    familyLabel(family) {
      return family === 'vehicle' ? this.$t('onboarding.equipment-profile.kind-vehicle') : this.kindLabel(family)
    },

    /** Véhicule : le service le plus simple d'abord (télématique). */
    familyChanged(family) {
      this.editing.kind = family === 'vehicle' ? VEHICLE_SERVICES[0] : family
      this.kindChanged(this.editing.kind)
    },

    kindChanged(kind) {
      if (!this.usagesFor(kind).includes(this.editing.usage)) this.editing.usage = this.usagesFor(kind)[0]
      this.editing.licences = {}
      this.editing.deviceTypes = []
      this.editing.someDeviceTypes = false
      if (!this.editing.id && !this.hasCompatibleSubscription) this.editing.licenceMode = 'new'
      this.loadPlans()
    },

    /** Hors domicile, une licence de remboursement déjà choisie ne vaut plus : on la retire. */
    usageChanged() {
      // Hors domicile, pas de membre à facturer : le choix est à refaire.
      if (this.editing.usage !== 'home') {
        BILLING_PARTS.forEach(part => {
          if (this.editing.billing[part].to === 'member') this.editing.billing[part] = { to: null, organizationId: null }
        })
      }
      const subscription = this.subscriptions.find(s => s.id === this.licence?.subscriptionId)
      if (subscription && this.effectiveLicenceTypes && !this.effectiveLicenceTypes.includes(subscription.licenceType)) this.editing.licences = {}
      if (!this.editing.id && !this.hasCompatibleSubscription) this.editing.licenceMode = 'new'
      this.loadPlans()
    },

    setDeviceTypesMode(mode) {
      this.editing.someDeviceTypes = mode === 'some'
      if (mode === 'all') this.editing.deviceTypes = []
      this.loadPlans()
    },

    deviceTypesChanged() {
      this.loadPlans()
    },

    licenceModeChanged() {
      this.loadPlans()
    },

    body() {
      const body = { name: this.editing.name.trim(), usage: this.editing.usage, access: this.grantsAccess ? this.editing.access : [] }
      if (this.deviceTypesOf(this.editing.kind).length) body.deviceTypes = this.editing.someDeviceTypes ? this.editing.deviceTypes : []
      if (this.editing.licenceMode === 'new') {
        body.newSubscription = subscriptionBody(this.editing.subscription, true)
        if (this.chargingServices) body.newSubscription.billing = this.billingBody()
      } else {
        body.licence = this.chargingServices ? { ...this.licence, billing: this.billingBody() } : this.licence
      }
      return body
    },

    async save() {
      if (!this.canSave || !this.$refs.form.validate()) return
      this.editing.running = true
      this.editing.error = null
      try {
        let id = this.editing.id
        if (id) {
          await EquipmentProfileRepository.update(this.organizationId, id, this.body())
        } else {
          id = (await EquipmentProfileRepository.create(this.organizationId, { ...this.body(), kind: this.editing.kind }))?.id || null
        }
        this.notifySuccess(this.editing.licenceMode === 'new' ? 'onboarding.equipment-profile.saved-with-subscription-ok' : 'onboarding.equipment-profile.saved-ok')
        this.$emit('input', false)
        this.$emit('saved', id)
      } catch (e) {
        this.editing.error = this.messageOf(e)
      } finally {
        this.editing.running = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.equipment-profile-editor {
  ::v-deep .v-navigation-drawer__content {
    overflow-y: auto;
    // Le contenu d'un tiroir est une colonne flex : la barre de titre s'étirait tant que le formulaire était court
    // (pendant le chargement), et tout descendait en bas du panneau.
    > * {
      flex: 0 0 auto;
    }
  }
}
</style>
